import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Typography, createStyles, makeStyles } from '@material-ui/core';
import { useEffect } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearUnregistrationSuccessMessage,
  fetchRegisteredUsers,
  fetchUserData,
  sendUnregisterUser,
} from '../../store/Notifications/NotificationsActions';
import { ApplicationState } from '../../store';
import * as React from 'react';
import { Button, ListItemButton } from '@mui/material';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import StringResourceManager, { GetString } from '../StringResourceManager';
import { NotificationUser } from '../../Interfaces/Notifications/NotificationUser';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { LoadingPanel } from '../LoadingPanel';

const useStyles = makeStyles(() =>
  createStyles({
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
  })
);

interface Props {
  onSelect: (username: string) => void;
  onClose: () => void;
  handleEditUser: (username: string) => void;
  listAccess: boolean;
}

const RegisteredUsersListBox: React.FC<Props> = ({
  onSelect,
  onClose,
  handleEditUser,
  listAccess,
}): React.ReactElement => {
  const classes = useStyles();

  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState('');
  const [successMessage, displaySuccessMessage] = React.useState(false);

  const UNREGISTER_USER_TITLE = GetString('UNREGISTER_USER_TITLE');
  const UNREGISTER_CONFIRMATION_MESSAGE = GetString(
    'USER_UNREGISTER_CONFIRMATION_MESSAGE'
  );
  const YES = GetString('YES');
  const NO = GetString('NO');

  const unregistrationStatus: string = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.unregistrationSuccessMessage
  );
  const handleClick = (username: string) => () => {
    setSelectedUser(username);
    onSelect(username);
  };
  const handleEdit = (username: string) => {
    setSelectedUser(username);
    handleEditUser(username);
  };
  const registeredUsers: NotificationUser[] = useSelector(
    (state: ApplicationState) => state.notificationsReducer.registeredUsers
  );
  const selectedUserData: NotificationUser = useSelector(
    (state: ApplicationState) => state.notificationsReducer.userData
  );
  const loading: boolean = useSelector(
    (state: ApplicationState) =>
      state.notificationsReducer.isFetchingRegisteredUsers
  );
  const dispatch: Dispatch<any> = useDispatch();
  useEffect((): ReturnType<React.EffectCallback> => {
    fetchRegisteredUsers(dispatch);
    return (): void => {};
  }, []);

  const handleDelete = (username: string) => () => {
    setSelectedUser(username);
    fetchUserData(dispatch, username);
    setShowDeleteDialog(true);
  };
  const handleClose = () => {
    setSelectedUser('');
    setShowDeleteDialog(false);
  };
  const unregisterUser = () => {
    let payload: NotificationUser = selectedUserData;
    sendUnregisterUser(dispatch, payload).then(() =>
      fetchRegisteredUsers(dispatch)
    );
    displaySuccessMessage(true);
    handleClose();
    onClose();
  };
  const resetSuccessMessage = () => {
    clearUnregistrationSuccessMessage(dispatch);
  };

  useEffect(() => {
    let timeoutId: any;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        displaySuccessMessage(false);
        resetSuccessMessage();
      }, 3000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [successMessage]);

  return (
    <React.Fragment>
      <List
        sx={{
          width: '100%',
          height: 350,
          maxWidth: 360,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
      >
        {loading && LoadingPanel}
        {registeredUsers.map((user, index) => (
          <ListItem key={index}>
            <ListItemButton
              selected={selectedUser === user.UserName}
              role={undefined}
              onClick={handleClick(user.UserName)}
              disabled={listAccess}
            >
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    style={{ whiteSpace: 'normal', overflow: 'hidden' }}
                  >
                    {user.UserName}
                  </Typography>
                }
              ></ListItemText>
            </ListItemButton>
            <IconButton
              onClick={() => handleEdit(user.UserName)}
              disabled={listAccess}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={handleDelete(user.UserName)}
              disabled={listAccess}
            >
              <DeleteIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>
      {showDeleteDialog && (
        <Dialog onClose={handleClose} title={UNREGISTER_USER_TITLE}>
          <Typography variant="body1">
            {UNREGISTER_CONFIRMATION_MESSAGE}
          </Typography>
          <DialogActionsBar layout="center">
            <Button
              color="primary"
              variant="contained"
              onClick={unregisterUser}
            >
              {YES}
            </Button>
            <Button color="primary" variant="contained" onClick={handleClose}>
              {NO}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Fade>
        <NotificationGroup className={classes.bottomRight}>
          {successMessage && unregistrationStatus && (
            <Notification
              className={classes.send}
              type={{
                style: unregistrationStatus === 'success' ? 'success' : 'error',
                icon: true,
              }}
              closable={true}
            >
              {
                <StringResourceManager
                  stringKey={
                    unregistrationStatus === 'success'
                      ? 'USER_UNREGISTRATION_SUCCESS_MESSAGE'
                      : 'USER_UNREGISTRATION_FAILED_MESSAGE'
                  }
                />
              }
            </Notification>
          )}
        </NotificationGroup>
      </Fade>
    </React.Fragment>
  );
};

export default RegisteredUsersListBox;
